import { CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { cn } from 'lib/utils';

const variantClasses = {
  primary:
    'bg-secondary !text-white hover:bg-secondary-darkened [&_svg]:stroke-current [&_svg]:fill-none',
  primaryDarkened: 'bg-second-button-card text-white hover:opacity-80',
  primaryLight:
    'bg-primary-emphasis-light text-white hover:bg-primary-darkened',
  secondary:
    'bg-primary-light text-primary-darkened hover:bg-primary-darkened hover:text-white [&_svg]:stroke-current [&_svg]:fill-none hover:[&_svg]:stroke-white',
  transparent:
    'bg-transparent text-black hover:bg-grey-lightest [&_svg]:stroke-current [&_svg]:fill-none',
  danger:
    'bg-semantic-danger text-white hover:opacity-70 disabled:bg-grey-lighter disabled:text-grey [&_svg]:stroke-current [&_svg]:fill-none',
  success:
    'bg-secondary-darkened text-white hover:brightness-75 [&_svg]:stroke-current [&_svg]:fill-none',
  default:
    'bg-grey text-white hover:bg-grey-light [&_svg]:stroke-current [&_svg]:fill-none'
};

const variantOutlinedClasses = {
  primary:
    '!bg-secondary-light !text-secondary-darkest border border-secondary [&_svg]:stroke-current [&_svg]:fill-none',
  primaryDarkened: '',
  primaryLight: '',
  secondary:
    '!bg-primary-lighter !text-primary-darkened border border-primary-darkened hover:!bg-primary-darkened hover:!text-white [&_svg]:stroke-current [&_svg]:fill-none',
  transparent: '',
  danger:
    '!bg-semantic-danger/20 !text-semantic-danger border border-semantic-danger hover:!bg-semantic-danger hover:!text-white [&_svg]:stroke-current [&_svg]:fill-none',
  success: '',
  default:
    '!bg-grey-lightest !text-black border border-grey hover:!bg-grey-lighter [&_svg]:stroke-current [&_svg]:fill-none'
};

const sizeClasses = {
  xxsmall:
    'px-1.5 py-px [&_span]:text-[calc(var(--font-size-body)-3px)]! [&_svg]:size-[11px]',
  xsmall:
    'px-2.5 py-0.5 [&_span]:text-[length:--font-size-body]! [&_svg]:size-[15px]',
  small:
    'px-4 py-0.5 [&_span]:text-[calc(var(--font-size-body)-1px)]! [&_svg]:size-[19px]',
  medium:
    'px-7 py-1.5 [&_span]:text-[calc(var(--font-size-body)+1px)]! [&_svg]:size-5',
  large:
    'px-11 py-2 [&_span]:text-[calc(var(--font-size-body)+2px)]! [&_svg]:size-[22px]'
};

const sizeOnlyIconClasses = {
  xxsmall: 'p-px',
  xsmall: 'p-0.5',
  small: 'p-1',
  medium: 'p-2',
  large: 'p-3'
};

const roundedClasses = {
  small: 'rounded-[5px]',
  medium: 'rounded-[10px]',
  large: 'rounded-[15px]'
};

function stripHoverClasses(classString) {
  if (!classString || typeof classString !== 'string') return classString ?? '';
  return classString
    .replaceAll(/\bhover:[^\s]+/g, '')
    .replaceAll(/\s+/g, ' ')
    .trim();
}

function variantClassWithOptionalHover(classes, noHoverEffects) {
  return noHoverEffects ? stripHoverClasses(classes) : classes;
}

const Button = forwardRef(function Button(
  {
    className,
    text,
    variant = 'primaryDarkened',
    size = 'medium',
    onClick = () => {},
    icon,
    rounded = 'small',
    outlined = false,
    fill,
    isLoading = false,
    isDisabled = false,
    loadingColor,
    type = 'button',
    disabled: disabledProp,
    noHoverEffects = false,
    iconPosition = 'left',
    href,
    ...buttonProps
  },
  ref
) {
  const disabled = isDisabled || disabledProp;
  // with href it renders a real link styled as a button, avoiding a button nested inside an anchor
  const isLink = Boolean(href);
  const Component = isLink ? 'a' : 'button';
  const elementProps = isLink
    ? {
        href: disabled ? undefined : href,
        'aria-disabled': disabled || undefined
      }
    : { type, disabled };

  const baseVariant =
    variant && variantClasses[variant] ? variantClasses[variant] : '';
  const variantClass = variantClassWithOptionalHover(
    baseVariant,
    noHoverEffects
  );

  const baseOutlined =
    outlined && variantOutlinedClasses[variant]
      ? variantOutlinedClasses[variant]
      : '';
  const outlinedClass = variantClassWithOptionalHover(
    baseOutlined,
    noHoverEffects
  );

  let disabledClassName = null;
  if (disabled) {
    if (noHoverEffects) {
      disabledClassName =
        'bg-grey-lighter! text-grey! cursor-not-allowed group';
    } else {
      disabledClassName =
        'bg-grey-lighter! text-grey! cursor-not-allowed hover:bg-grey-lighter! hover:text-grey! group';
    }
  }

  return (
    <Component
      ref={ref}
      {...elementProps}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 font-normal border border-solid border-transparent outline-none transition-all duration-200 w-max min-w-0',
        'active:scale-[0.99]',
        !noHoverEffects && 'cursor-pointer',
        fill && 'w-full',
        variantClass,
        outlinedClass,
        sizeClasses[size],
        icon && !text && sizeOnlyIconClasses[size],
        rounded && roundedClasses[rounded],
        disabledClassName,
        className
      )}
      onClick={onClick}
      {...buttonProps}
    >
      {isLoading ? (
        <CircularProgress
          style={loadingColor ? { color: loadingColor } : {}}
          size='small'
        />
      ) : (
        icon &&
        iconPosition === 'left' && (
          <span className='inline-flex shrink-0 [&_svg]:shrink-0 [&_svg]:stroke-current [&_svg]:fill-none group-hover:[&_svg]:stroke-current!'>
            {icon}
          </span>
        )
      )}
      {text && <span className='pt-0.5'>{text}</span>}
      {icon && iconPosition === 'right' && (
        <span className='inline-flex shrink-0 [&_svg]:shrink-0 [&_svg]:stroke-current [&_svg]:fill-none group-hover:[&_svg]:stroke-current!'>
          {icon}
        </span>
      )}
    </Component>
  );
});

Button.propTypes = {
  className: PropTypes.string,
  text: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'primaryDarkened',
    'primaryLight',
    'secondary',
    'transparent',
    'danger',
    'success',
    'default'
  ]),
  type: PropTypes.string,
  size: PropTypes.oneOf(['xxsmall', 'xsmall', 'small', 'medium', 'large']),
  rounded: PropTypes.oneOf(['small', 'medium', 'large']),
  outlined: PropTypes.bool,
  fill: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  icon: PropTypes.element,
  loadingColor: PropTypes.string,
  noHoverEffects: PropTypes.bool,
  href: PropTypes.string,
  iconPosition: PropTypes.oneOf(['left', 'right'])
};

export default Button;
